'use client'

import { useMeasure } from '@react-hookz/web'
import { animated, useSpring } from '@react-spring/web'
import { Button } from '@snowball-tech/fractal/dist/src/components/Button'
import { Typography } from '@snowball-tech/fractal/dist/src/components/Typography'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'
import { UilAngleDown as ToggleSubEntriesIcon } from '@tooni/iconscout-unicons-react'

import { type MouseEvent, useEffect, useState } from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'

import type { ChildrenProps, SidebarEntry } from '../../types'

import { getUrl } from '../../helpers/navigation'
import { usePathname } from '../../helpers/translations'
import useAppName from '../../hooks/useAppName'
import useTranslations from '../../hooks/useTranslations'
import RouterButton from '../RouterButton'

type Props = {
  color?: 'primary' | 'secondary'
  expandSubEntries?: boolean
  fullWidth?: boolean
  onClick?: () => void
  onToggle?: () => void
} & Omit<SidebarEntry, 'subEntries'> &
  Partial<ChildrenProps>
export default function SidebarEntry({
  appName,
  children,
  collapsed = false,
  color = 'secondary',
  disabled = false,
  expandSubEntries = false,
  fullQualify,
  fullWidth = false,
  hardLink = false,
  href,
  icon,
  label,
  onClick,
  onToggle,
  target,
}: Props) {
  const pathname = usePathname()
  const $t = useTranslations()

  const currentAppName = useAppName()

  const hasSubEntries = Boolean(children)
  const isActive =
    ((isEmpty(currentAppName) ||
      isEmpty(appName) ||
      currentAppName === appName) &&
      (isEmpty(href) ? pathname === href : pathname.startsWith(href))) ||
    (hasSubEntries && expandSubEntries)

  const [isCollapsed, setIsCollapsed] = useState(
    !hasSubEntries || (!expandSubEntries && !isActive),
  )

  useEffect(() => {
    if (hasSubEntries) {
      setIsCollapsed(!isActive)
    }
  }, [isActive, hasSubEntries])

  const actualHref =
    ((isEmpty(appName) || currentAppName === appName) && !fullQualify) ||
    href?.startsWith('http')
      ? href
      : getUrl(appName ?? currentAppName, href)

  const actualTarget =
    !isEmpty(appName) && currentAppName !== appName ? '_blank' : target

  const [
    { height: childrenContainerHeight } = { height: 0 },
    childrenContainer,
  ] = useMeasure<HTMLDivElement>()

  const toggleAnimatedStyle = useSpring({
    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
  })
  const childrenAnimatedStyle = useSpring({
    // Use `1px` for the collapsed height to avoid a bug that happens when the
    // initial height is 0.
    // Add 16px to the expanded height to account for the padding of the
    // children elements container.
    height: isCollapsed ? '1px' : `${childrenContainerHeight + 16}px`,
    marginTop: isCollapsed ? 0 : 10,
  })

  const toggleCollapse = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()

    setIsCollapsed((isCurrentlyCollapsed) => !isCurrentlyCollapsed)

    if (isFunction(onToggle)) {
      setTimeout(onToggle, 2000)
    }
  }

  return (
    <div>
      <div className="flex justify-between">
        <RouterButton
          buttonClassName={cj(
            `justify-start [&>div]:justify-start`,
            color === 'primary' && !disabled ? 'text-dark' : '',
            color === 'secondary' && !disabled
              ? 'text-grey-30 hover:text-white'
              : '',
            color === 'primary' && !disabled && isActive ? '' : '',
            color === 'secondary' && !disabled && isActive
              ? 'text-white [&_svg]:text-decorative-purple-70'
              : '',
            !disabled && isActive ? '' : '',
          )}
          className="group no-underline"
          disabled={disabled}
          fullWidth
          hardLink={hardLink}
          href={disabled ? undefined : actualHref}
          icon={icon}
          iconPosition="left"
          target={actualTarget}
          title={label}
          underlined={false}
          variant="text"
          onClick={onClick}
        >
          <Typography
            className="group-hover:font-weight-median overflow-hidden text-left"
            variant={
              fullWidth ? (isActive ? 'body-1-median' : 'body-1') : 'body-2'
            }
          >
            {label}
          </Typography>
        </RouterButton>

        {!disabled && hasSubEntries && !collapsed && (
          <animated.div style={toggleAnimatedStyle}>
            <Button
              className={cj(
                color === 'primary'
                  ? 'text-dark'
                  : 'text-grey-30 hover:text-white',
              )}
              icon={<ToggleSubEntriesIcon />}
              iconOnly
              label={
                isCollapsed ? $t('general.expand') : $t('general.collapse')
              }
              variant="text"
              onClick={(event) => toggleCollapse(event)}
            />
          </animated.div>
        )}
      </div>

      {hasSubEntries && !collapsed && (
        <animated.div
          className={cj(
            'overflow-hidden',
            color === 'primary' ? 'border-normal' : 'border-grey-30',
            isCollapsed ? '' : 'border-t-1 py-1',
          )}
          style={childrenAnimatedStyle}
        >
          <div ref={childrenContainer} className="flex flex-col gap-1">
            {children}
          </div>
        </animated.div>
      )}
    </div>
  )
}
